import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Mail,
  Wifi,
  RefreshCcw,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Save,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API_URL = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 15;

const TIPO_LABEL = {
  resultado: "Resultado",
  resultado_orden: "Resultados de orden",
  factura: "Factura",
};

const estadoBadge = {
  enviado: "bg-emerald-100 text-emerald-700",
  error: "bg-red-100 text-red-700",
  pendiente: "bg-amber-100 text-amber-700",
};

const EmailPage = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // ── Conexión SMTP ───────────────────────────────────────────
  const [config, setConfig] = useState({ activo: false });
  const [form, setForm] = useState({
    proveedor: "gmail",
    usuario_correo: "",
    correo_remitente: "",
    pass_app: "",
    nombre_remitente: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/email/config`, { headers });
      setConfig(data);
      setForm((f) => ({
        ...f,
        proveedor: data.proveedor || "gmail",
        usuario_correo: data.usuario_correo || "",
        correo_remitente: data.correo_remitente || "",
        nombre_remitente: data.nombre_remitente || "",
      }));
    } catch (_) {}
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/email/config`, form, { headers });
      toast.success("Configuración guardada. Prueba la conexión para activarla.");
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_URL}/email/config/test`, {}, { headers });
      toast.success(data.message || "Conexión exitosa");
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error probando la conexión");
    } finally {
      setTesting(false);
    }
  };

  // ── Plantillas ──────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/email/templates`, { headers });
      setTemplates(data);
    } catch (_) {
      toast.error("Error cargando plantillas");
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const updateTemplateField = (tipo, field, value) => {
    setTemplates((prev) => prev.map((t) => (t.tipo === tipo ? { ...t, [field]: value } : t)));
  };

  const handleSaveTemplate = async (tipo) => {
    const template = templates.find((t) => t.tipo === tipo);
    if (!template) return;
    setSavingTemplate(tipo);
    try {
      await axios.put(
        `${API_URL}/email/templates/${tipo}`,
        { asunto: template.asunto, cuerpo_html: template.cuerpo_html },
        { headers },
      );
      toast.success("Plantilla guardada");
    } catch (err) {
      toast.error(err.response?.data?.error || "Error guardando plantilla");
    } finally {
      setSavingTemplate(null);
    }
  };

  // ── Historial de envíos ─────────────────────────────────────
  const [envios, setEnvios] = useState([]);
  const [totalEnvios, setTotalEnvios] = useState(0);
  const [pageEnvios, setPageEnvios] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [loadingEnvios, setLoadingEnvios] = useState(false);

  const fetchEnvios = useCallback(async () => {
    setLoadingEnvios(true);
    try {
      const params = { page: pageEnvios, pageSize: PAGE_SIZE };
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroEstado) params.estado = filtroEstado;
      const { data } = await axios.get(`${API_URL}/email/envios`, { headers, params });
      setEnvios(data.items || []);
      setTotalEnvios(data.total || 0);
    } catch (_) {
      toast.error("Error cargando historial de envíos");
    } finally {
      setLoadingEnvios(false);
    }
  }, [pageEnvios, filtroTipo, filtroEstado]); // eslint-disable-line

  useEffect(() => {
    fetchEnvios();
  }, [fetchEnvios]);

  const totalPages = Math.max(1, Math.ceil(totalEnvios / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* ── Card de estado / conexión SMTP ─────────────────── */}
      <div
        className={`rounded-2xl border p-5 ${config.activo ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-xl ${config.activo ? "bg-emerald-100" : "bg-gray-100"}`}>
            <Mail className={`h-7 w-7 ${config.activo ? "text-emerald-600" : "text-gray-400"}`} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-800">Configuración de correo</h2>
            <p className={`text-sm font-medium ${config.activo ? "text-emerald-600" : "text-gray-500"}`}>
              {config.activo ? "Conectado" : "Sin configurar / no probado"}
            </p>
            {config.activo && config.usuario_correo && (
              <p className="text-xs text-gray-500 mt-0.5">{config.usuario_correo}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
            <select
              value={form.proveedor}
              onChange={(e) => setForm((f) => ({ ...f, proveedor: e.target.value }))}
              className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            >
              <option value="gmail">Gmail</option>
              <option value="outlook">Outlook</option>
              <option value="brevo">Brevo</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nombre remitente</label>
            <input
              type="text"
              value={form.nombre_remitente}
              onChange={(e) => setForm((f) => ({ ...f, nombre_remitente: e.target.value }))}
              placeholder="Ej: Laboratorio Vetronix"
              className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {form.proveedor === "brevo" ? "Login SMTP (Brevo)" : "Correo"}
            </label>
            <input
              type={form.proveedor === "brevo" ? "text" : "email"}
              value={form.usuario_correo}
              onChange={(e) => setForm((f) => ({ ...f, usuario_correo: e.target.value }))}
              placeholder={form.proveedor === "brevo" ? "xxxxxxxxx@smtp-brevo.com" : "laboratorio@gmail.com"}
              className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            />
            {form.proveedor === "brevo" && (
              <p className="text-xs text-gray-400 mt-1">
                Está en Brevo &gt; Configuración &gt; SMTP &amp; API, pestaña SMTP (campo "Login").
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {form.proveedor === "brevo" ? "SMTP key (Brevo)" : "Contraseña de aplicación"}
            </label>
            <input
              type="password"
              value={form.pass_app}
              onChange={(e) => setForm((f) => ({ ...f, pass_app: e.target.value }))}
              placeholder="••••••••••••••••"
              className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            />
          </div>
          {form.proveedor === "brevo" && (
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Correo remitente (verificado en Brevo)
              </label>
              <input
                type="email"
                value={form.correo_remitente}
                onChange={(e) => setForm((f) => ({ ...f, correo_remitente: e.target.value }))}
                placeholder="laboratorio@tudominio.com"
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              <p className="text-xs text-gray-400 mt-1">
                Debe estar verificado en Brevo &gt; Remitentes, dominios &amp; dedicated IPs &gt; Remitentes. Si dejas vacío, el envío será rechazado (el login SMTP no es un remitente válido).
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <Button onClick={handleSaveConfig} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </Button>
          <Button variant="outline" onClick={handleTestConnection} disabled={testing} className="gap-2">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
            Probar conexión
          </Button>
        </div>
      </div>

      {/* ── Plantillas ───────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-gray-800">Plantillas de correo</h3>
        {templates.map((t) => (
          <div key={t.tipo} className="rounded-2xl border border-brand-100 bg-white p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700">{TIPO_LABEL[t.tipo] || t.tipo}</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Asunto</label>
              <input
                type="text"
                value={t.asunto}
                onChange={(e) => updateTemplateField(t.tipo, "asunto", e.target.value)}
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cuerpo (HTML)</label>
              <textarea
                value={t.cuerpo_html}
                onChange={(e) => updateTemplateField(t.tipo, "cuerpo_html", e.target.value)}
                rows={4}
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 font-mono"
              />
            </div>
            <p className="text-xs text-gray-400">
              Placeholders disponibles: {"{empresa}"}, {"{paciente}"}, {"{cliente}"}, {"{propietario}"}, {"{examen_cod}"}, {"{factura_id}"}, {"{destinatario}"}
            </p>
            <Button
              size="sm"
              onClick={() => handleSaveTemplate(t.tipo)}
              disabled={savingTemplate === t.tipo}
              className="gap-2"
            >
              {savingTemplate === t.tipo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Guardar plantilla
            </Button>
          </div>
        ))}
      </div>

      {/* ── Historial de envíos ─────────────────────────── */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Historial de envíos</h3>
            <p className="text-xs text-gray-500">{totalEnvios} registro{totalEnvios !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-brand-400" />
            <select
              value={filtroTipo}
              onChange={(e) => {
                setFiltroTipo(e.target.value);
                setPageEnvios(1);
              }}
              className="text-sm border border-brand-200 rounded-xl px-3 py-1.5 bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">Todo tipo</option>
              <option value="resultado">Resultado</option>
              <option value="resultado_orden">Resultados de orden</option>
              <option value="factura">Factura</option>
            </select>
            <select
              value={filtroEstado}
              onChange={(e) => {
                setFiltroEstado(e.target.value);
                setPageEnvios(1);
              }}
              className="text-sm border border-brand-200 rounded-xl px-3 py-1.5 bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">Todo estado</option>
              <option value="enviado">Enviados</option>
              <option value="error">Con error</option>
              <option value="pendiente">Pendientes</option>
            </select>
            <button
              onClick={fetchEnvios}
              className="p-1.5 rounded-xl border border-brand-200 bg-brand-50 text-brand-500 hover:text-brand-700 transition-all"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-brand-100">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-brand-gradient text-white">
                {["Tipo", "Referencia", "Correo", "Estado", "Enviado por", "Fecha"].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap text-left">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {loadingEnvios ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center">
                    <div className="flex flex-col items-center gap-2 text-brand-400">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-sm">Cargando historial…</span>
                    </div>
                  </td>
                </tr>
              ) : envios.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center text-gray-400 italic text-sm">
                    No hay envíos registrados.
                  </td>
                </tr>
              ) : (
                envios.map((e, i) => (
                  <tr key={e.id} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                        {TIPO_LABEL[e.tipo] || e.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">
                      {e.tipo === "factura" ? e.factura_id : `${e.paciente_cod}${e.examen_cod ? ` / ${e.examen_cod}` : ""}`}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{e.correo_destino}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${estadoBadge[e.estado] || "bg-gray-100 text-gray-600"}`}>
                        {e.estado === "enviado" && <CheckCircle2 className="h-3 w-3" />}
                        {e.estado === "error" && <XCircle className="h-3 w-3" />}
                        {e.estado === "pendiente" && <Clock className="h-3 w-3" />}
                        {e.estado}
                      </span>
                      {e.error_msg && (
                        <p className="text-xs text-red-500 mt-0.5 max-w-[200px] truncate" title={e.error_msg}>
                          {e.error_msg}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{e.enviado_por || "—"}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {e.fecha_envio ? new Date(e.fecha_envio).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{envios.length} de {totalEnvios}</span>
            <div className="flex items-center gap-2">
              <button
                className="p-1.5 rounded-lg border border-brand-200 disabled:opacity-40 hover:bg-brand-50 transition-all"
                disabled={pageEnvios <= 1}
                onClick={() => setPageEnvios((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">
                {pageEnvios}/{totalPages}
              </span>
              <button
                className="p-1.5 rounded-lg border border-brand-200 disabled:opacity-40 hover:bg-brand-50 transition-all"
                disabled={pageEnvios >= totalPages}
                onClick={() => setPageEnvios((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailPage;
